import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { lazy, Suspense } from "react";
import PageTransition from "./components/PageTransition";
import AnimatedBackground from "./components/AnimatedBackground";
import LanguageLayout from "./components/LanguageLayout";
import LangRedirect from "./components/LangRedirect";

const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Purchase = lazy(() => import("./pages/Purchase"));
const Version = lazy(() => import("./pages/Version"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));

const queryClient = new QueryClient();

// API Version Component - Returns pure JSON
const ApiVersion = () => {
  const versionData = {
    "record": {
      "usermode-ver": ["1.0.0.0"],
      "kernel-ver": ["1.0.0.0"],
      "launcher-ver": ["1.0.0.0"],
      "status": "undetected",
      "news-type": 0,
      "news": "این یک فیلد خبری جدید است"
    },
    "metadata": {
      "id": "690e4759ae596e708f4b20b3",
      "private": false,
      "createdAt": "2025-11-07T19:24:09.318Z",
      "name": "Ziqo"
    }
  };

  return (
    <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre', padding: 0 }}>
      {JSON.stringify(versionData)}
    </pre>
  );
};

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <Routes location={location} key={location.pathname}>
      {/* Root redirect based on IP */}
      <Route path="/" element={<LangRedirect />} />
      
      {/* API routes (no lang prefix) */}
      <Route path="/api/version" element={<ApiVersion />} />
      
      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageLayout />}>
        <Route index element={<PageTransition><Index /></PageTransition>} />
        <Route path="auth" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Auth /></PageTransition></Suspense>} />
        <Route path="dashboard" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Dashboard /></PageTransition></Suspense>} />
        <Route path="purchase" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Purchase /></PageTransition></Suspense>} />
        <Route path="version" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Version /></PageTransition></Suspense>} />
        <Route path="blog" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Blog /></PageTransition></Suspense>} />
        <Route path="blog/:slug" element={<Suspense fallback={<LoadingFallback />}><PageTransition><BlogArticle /></PageTransition></Suspense>} />
        <Route path="about" element={<Suspense fallback={<LoadingFallback />}><PageTransition><About /></PageTransition></Suspense>} />
        <Route path="contact" element={<Suspense fallback={<LoadingFallback />}><PageTransition><Contact /></PageTransition></Suspense>} />
      </Route>
      
      {/* Catch-all */}
      <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
    </Routes>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AnimatedBackground />
                <AnimatedRoutes />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
